import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';

interface GalleryItem {
	type: 'video' | 'image';
	id: string;
	platform?: 'youtube' | 'vimeo';
	url: string;
	thumbnailUrl: string;
	embedUrl?: string;
	alt?: string;
	title?: string;
}

interface VariationGalleryData {
	videos: GalleryItem[];
	images: GalleryItem[];
}

interface GalleryData {
	videos: GalleryItem[];
	images: GalleryItem[];
	is_variable: boolean;
	variation_data: Record<number, VariationGalleryData>;
}

class ProductGallery {
	private swiper: Swiper | null = null;
	private currentItems;
	private galleryData: GalleryData;
	private readonly mainDisplay;
	private readonly zoomDialog: HTMLDialogElement|null;
	private readonly zoomDisplay;
	private currentZoomIndex = 0;

	constructor(private readonly element: Element) {
		this.mainDisplay = element.querySelector('.gallery-main');
		this.zoomDialog = document.querySelector<HTMLDialogElement>('.gallery-zoom-dialog');
		this.zoomDisplay = this.zoomDialog?.querySelector<HTMLElement>('.dialog-display');

		const dataAttr = element.getAttribute('data-gallery');
		this.galleryData = dataAttr ? JSON.parse(dataAttr) : {
			videos: [],
			images: [],
			is_variable: false,
			variation_data: {}
		};

		this.currentItems = [...this.galleryData.images, ...this.galleryData.videos];
	}

	init(): void {
		this.initSwiper();
		this.bindThumbnailClicks();
		this.bindPlayButtons();
		this.bindZoomButtons();
		this.bindDialogButtons();

		if (this.galleryData.is_variable) {
			this.listenForVariationChanges();
		}
	}

	private initSwiper(): void {
		const swiperElement = this.element.querySelector('.gallery-thumbnails');
		if (!swiperElement) return;

		this.swiper = new Swiper(swiperElement as HTMLElement, {
			modules: [Navigation],
			slidesPerView: 'auto',
			spaceBetween: 8,
			navigation: {
				nextEl: this.element.querySelector('.swiper-button-next') as HTMLElement,
				prevEl: this.element.querySelector('.swiper-button-prev') as HTMLElement,
			},
			breakpoints: {
				320: {
					slidesPerView: 2,
					spaceBetween: 8
				},
				640: {
					slidesPerView: 3,
					spaceBetween: 8
				},
				768: {
					slidesPerView: 4,
					spaceBetween: 8
				},
				1024: {
					slidesPerView: 5,
					spaceBetween: 8
				}
			}
		});
	}

	private bindThumbnailClicks(): void {
		const thumbnails = this.element.querySelectorAll('.thumbnail-item');
		thumbnails.forEach((thumbnail) => {
			thumbnail.addEventListener('click', () => {
				const index = parseInt(thumbnail.getAttribute('data-index') || '0');
				this.switchToItem(index);
				
				thumbnails.forEach((t) => {
					t.classList.remove('active');
				});
				thumbnail.classList.add('active');
			});
		});
	}

	private bindPlayButtons(): void {
		const playButtons = this.element.querySelectorAll('.play-button');
		playButtons.forEach((button) => {
			button.addEventListener('click', (e) => {
				e.stopPropagation();
				const placeholder = button.closest('.video-placeholder') as HTMLElement;
				if (placeholder) {
					this.loadVideoIframe(placeholder);
				}
			});
		});
	}

	private bindZoomButtons(): void {
		const zoomButtons = this.element.querySelectorAll('.zoom-button');
		zoomButtons.forEach((button) => {
			button.addEventListener('click', (e) => {
				e.stopPropagation();
				const itemIndex = this.getCurrentItemIndex();
				this.openZoomDialog(itemIndex);
			});
		});
	}

	private bindDialogButtons(): void {
		if (!this.zoomDialog) return;

		// Close button
		const closeButton = this.zoomDialog.querySelector('.dialog-close') as HTMLElement;
		if (closeButton) {
			closeButton.addEventListener('click', () => {
				this.closeZoomDialog();
			});
		}

		// Previous button
		const prevButton = this.zoomDialog.querySelector('.dialog-prev') as HTMLElement;
		if (prevButton) {
			prevButton.addEventListener('click', () => {
				this.navigateZoomDialog(-1);
			});
		}

		// Next button
		const nextButton = this.zoomDialog.querySelector('.dialog-next') as HTMLElement;
		if (nextButton) {
			nextButton.addEventListener('click', () => {
				this.navigateZoomDialog(1);
			});
		}

		// Close on backdrop click
		this.zoomDialog.addEventListener('click', (e) => {
			if (e.target === this.zoomDialog) {
				this.closeZoomDialog();
			}
		});

		// Keyboard navigation
		document.addEventListener('keydown', (e) => {
			if (!this.zoomDialog?.open) return;

			switch (e.key) {
				case 'Escape':
					this.closeZoomDialog();
					break;
				case 'ArrowLeft':
					this.navigateZoomDialog(-1);
					break;
				case 'ArrowRight':
					this.navigateZoomDialog(1);
					break;
			}
		});
	}

	private getCurrentItemIndex(): number {
		const activeThumbnail = this.element.querySelector('.thumbnail-item.active') as HTMLElement;
		if (activeThumbnail) {
			return parseInt(activeThumbnail.getAttribute('data-index') || '0');
		}
		return 0;
	}

	private openZoomDialog(index: number): void {
		console.log('openZoomDialog', index);
		console.log('this.zoomDialog', this.zoomDialog);
		console.log('this.zoomDisplay', this.zoomDisplay);
		console.log('this.currentItems', this.currentItems);
		console.log('index >= this.currentItems.length', index >= this.currentItems.length);
		if (!this.zoomDialog || !this.zoomDisplay || index >= this.currentItems.length)
			return;

		console.log('currentItems', this.currentItems);

		this.currentZoomIndex = index;
		this.updateZoomDisplay();
		this.zoomDialog.showModal();
		document.body.style.overflow = 'hidden'; // Prevent background scrolling
	}

	private closeZoomDialog(): void {
		if (!this.zoomDialog) return;

		this.zoomDialog.close();
		document.body.style.overflow = '';
		
		if (this.zoomDisplay) {
			this.zoomDisplay.innerHTML = '';
		}
	}

	private navigateZoomDialog(direction: number): void {
		const newIndex = this.currentZoomIndex + direction;
		if (newIndex >= 0 && newIndex < this.currentItems.length) {
			this.currentZoomIndex = newIndex;
			this.updateZoomDisplay();
			this.switchToItem(newIndex); // Sync with main gallery
		}
	}

	private updateZoomDisplay(): void {
		if (!this.zoomDisplay) return;

		const item = this.currentItems[this.currentZoomIndex];
		if (!item) return;

		let content: string;

		if (item.type === 'video') {
			// For videos, show the embedded iframe directly
			const embedUrl = item.embedUrl + '?autoplay=0&rel=0';
			content = `<div class="zoom-video">
				<iframe src="${this.escapeHtml(embedUrl)}"
						frameborder="0"
						allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
						allowfullscreen>
				</iframe>
			</div>`;
		} else {
			// For images, show the full resolution image
			content = `<div class="zoom-image">
				<img src="${this.escapeHtml(item.url)}" alt="${this.escapeHtml(item.alt || item.title || 'Zoomed image')}" />
			</div>`;
		}

		this.zoomDisplay.innerHTML = content;
	}

	private renderMainDisplayItem(item: GalleryItem): HTMLElement {
		const templateId = item.type === 'video' ? 'video-main-template' : 'image-main-template';
		const template = document.getElementById(templateId) as HTMLTemplateElement;

		if (!template) {
			throw new Error(`Template ${templateId} not found`);
		}

		const clone = template.content.cloneNode(true) as DocumentFragment;
		const element = clone.firstElementChild as HTMLElement;

		if (item.type === 'video') {
			element.setAttribute('data-embed-url', item.embedUrl || '');
			const img = element.querySelector('img')!;
			img.src = item.thumbnailUrl;
		} else {
			const img = element.querySelector('img')!;
			img.src = item.url;
			img.alt = item.alt || '';
		}

		return element;
	}

	private switchToItem(index: number): void {
		if (!this.mainDisplay || index >= this.currentItems.length) return;

		const item = this.currentItems[index];
		const displayElement = this.renderMainDisplayItem(item);
		this.mainDisplay.innerHTML = '';
		this.mainDisplay.appendChild(displayElement);

		this.bindZoomButtons();
		if (item.type === 'video') {
			this.bindPlayButtons();
		}
	}

	private loadVideoIframe(placeholder: HTMLElement): void {
		const embedUrl = placeholder.getAttribute('data-embed-url');
		if (!embedUrl) return;

		const iframe = document.createElement('iframe');
		iframe.src = embedUrl + '?autoplay=1';
		iframe.setAttribute('frameborder', '0');
		iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture');
		iframe.setAttribute('allowfullscreen', '');
		iframe.style.width = '100%';
		iframe.style.height = '100%';

		placeholder.innerHTML = '';
		placeholder.appendChild(iframe);
		placeholder.classList.remove('video-placeholder');
		placeholder.classList.add('video-iframe');
	}

	private listenForVariationChanges(): void {
		const variationIdInput = document.querySelector<HTMLInputElement>('input[name="variation_id"]');
		if (!variationIdInput)
			return;

		let lastVariationId: string | null = null;
		const handleVariationChange = () => {
			const currentVariationId = variationIdInput.value;

			if (currentVariationId && currentVariationId !== lastVariationId) {
				const variationId = parseInt(currentVariationId, 10);
				if (variationId && this.galleryData.variation_data[variationId]) {
					this.updateGalleryForVariation(variationId);
				}
				lastVariationId = currentVariationId;
			} else if (!currentVariationId && lastVariationId) {
				this.resetToDefaultGallery();
				lastVariationId = null;
			}
		};

		variationIdInput.addEventListener('change', handleVariationChange);
		variationIdInput.addEventListener('input', handleVariationChange);

		const observer = new MutationObserver(() => {
			handleVariationChange();
		});

		observer.observe(variationIdInput, {
			attributes: true,
			attributeFilter: ['value']
		});
	}

	private updateGalleryForVariation(variationId: number): void {
		const variationData = this.galleryData.variation_data[variationId];
		if (!variationData) return;

		this.currentItems = [
			...variationData.images,
			...variationData.videos,
			...this.galleryData.images,
			...this.galleryData.videos
		];

		this.rebuildGallery();
	}

	private resetToDefaultGallery(): void {
		this.currentItems = [...this.galleryData.images, ...this.galleryData.videos];
		this.rebuildGallery();
	}

	private rebuildGallery(): void {
		if (!this.mainDisplay) return;

		if (this.currentItems.length > 0) {
			this.switchToItem(0);
		}

		const swiperWrapper = this.element.querySelector('.swiper-wrapper');
		if (swiperWrapper) {
			swiperWrapper.innerHTML = this.currentItems.map((item, index) => `
				<div class="swiper-slide">
					<div class="thumbnail-item ${index === 0 ? 'active' : ''}" 
						 data-index="${index}"
						 data-type="${item.type}"
						 ${item.type === 'video' ? `data-embed-url="${this.escapeHtml(item.embedUrl || '')}" data-platform="${item.platform || ''}"` : ''}
						 data-url="${this.escapeHtml(item.url)}"
						 data-thumbnail-url="${this.escapeHtml(item.thumbnailUrl)}">
						<img src="${this.escapeHtml(item.thumbnailUrl)}" alt="${this.escapeHtml(item.alt || item.title || 'Thumbnail')}" />
						${item.type === 'video' ? `
							<div class="video-icon">
								<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
									<circle cx="12" cy="12" r="10" fill="rgba(0,0,0,0.6)"/>
									<path d="M10 8L16 12L10 16V8Z" fill="white"/>
								</svg>
							</div>
						` : ''}
					</div>
				</div>
			`).join('');

			if (this.swiper) {
				this.swiper.update();
			}

			this.bindThumbnailClicks();
			this.bindZoomButtons();
		}
	}

	private escapeHtml(text: string): string {
		const div = document.createElement('div');
		div.textContent = text;
		return div.innerHTML;
	}
}

document.addEventListener('DOMContentLoaded', () => {
	const galleries = document.querySelectorAll<HTMLElement>('.msa-product-gallery');
	galleries.forEach((galleryElement) => {
		const gallery = new ProductGallery(galleryElement as HTMLElement);
		gallery.init();
	});
});
